import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { trpc } from '../lib/trpc'
import { Button } from './ui/primitives'
import { hintergrundSchliesst } from './ui/hintergrund'

type Art = 'ok' | 'warn' | 'bad' | 'idle'
type Reiter = 'status' | 'deploys' | 'logs' | 'variablen'
type Aktion = 'ausrollen' | 'ohneCache' | 'neustart' | 'stopp' | 'start'
type DeployStand = 'wartet' | 'laeuft' | 'fertig' | 'fehler' | 'abgebrochen' | 'unbekannt'

const CHIP: Record<Art, string> = {
  ok: 'bg-good-bg text-good-text',
  warn: 'bg-[#fdf1ea] text-warn',
  bad: 'bg-bad-bg text-bad',
  idle: 'bg-[#f3f2ef] text-muted',
}

function Chip({ art, children }: { art: Art; children: React.ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-px text-[10.5px] font-semibold whitespace-nowrap ${CHIP[art]}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {children}
    </span>
  )
}

const DEPLOY: Record<DeployStand, { text: string; art: Art }> = {
  wartet: { text: 'wartet', art: 'idle' },
  laeuft: { text: 'läuft', art: 'warn' },
  fertig: { text: 'fertig', art: 'ok' },
  fehler: { text: 'fehlgeschlagen', art: 'bad' },
  abgebrochen: { text: 'abgebrochen', art: 'idle' },
  unbekannt: { text: 'unbekannt', art: 'idle' },
}

const AUSLOESER = { webhook: 'Push', api: 'aus Work/API', hand: 'von Hand' } as const

const AKTION_TEXT: Record<Aktion, string> = {
  ausrollen: 'Ausrollen angestoßen.',
  ohneCache: 'Neubau ohne Cache angestoßen.',
  neustart: 'Neustart angestoßen.',
  stopp: 'Anwendung wird gestoppt.',
  start: 'Anwendung wird gestartet.',
}

function wann(iso: string | null): string {
  if (!iso) return '–'
  const d = new Date(iso)
  const min = Math.round((Date.now() - d.getTime()) / 60_000)
  if (min < 1) return 'gerade eben'
  if (min < 60) return `vor ${min} Min`
  if (min < 24 * 60) return `vor ${Math.round(min / 60)} Std`
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function dauer(von: string | null, bis: string | null): string {
  if (!von || !bis) return ''
  const s = Math.round((new Date(bis).getTime() - new Date(von).getTime()) / 1000)
  if (s <= 0) return ''
  return s < 90 ? `${s} s` : `${Math.round(s / 60)} Min`
}

/**
 * **Das Coolify-Fenster** (04.11.2026). Nils: *„dann müsste ich mich nicht immer in coolify
 * einloggen um denen den status zu geben."*
 *
 * Status mit den wichtigen Knöpfen, Deploys mit Build-Log, Laufzeit-Log und – fürs Team – die
 * Umgebungsvariablen. Alles läuft live durch Work zu Coolify; gespeichert wird hier nichts.
 * „Für Claude kopieren" packt den Stand als Text in die Zwischenablage.
 */
export function CoolifyFenster({
  umgebungId,
  label,
  darfAendern,
  schliessen,
}: {
  umgebungId: string
  label: string
  darfAendern: boolean
  schliessen: () => void
}) {
  const [reiter, setReiter] = useState<Reiter>('status')
  const queryClient = useQueryClient()

  const betrieb = useQuery({
    queryKey: ['umgebung', 'betrieb', umgebungId],
    queryFn: () => trpc.umgebung.betrieb.query({ umgebungId }),
    retry: false,
    /** Solange ein Deploy wartet oder läuft, alle 4 s nachsehen – sonst nur auf Zuruf. */
    refetchInterval: (q) =>
      q.state.data?.deploys.some((d) => d.stand === 'wartet' || d.stand === 'laeuft')
        ? 4000
        : false,
  })
  const s = betrieb.data

  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopImmediatePropagation()
      schliessen()
    }
    window.addEventListener('keydown', taste, true)
    return () => window.removeEventListener('keydown', taste, true)
  }, [schliessen])

  const [kopie, setKopie] = useState<'laedt' | 'kopiert' | { text: string } | null>(null)
  const fuerClaude = async () => {
    setKopie('laedt')
    try {
      const { text } = await queryClient.fetchQuery({
        queryKey: ['umgebung', 'bericht', umgebungId],
        queryFn: () => trpc.umgebung.bericht.query({ umgebungId }),
        staleTime: 0,
      })
      try {
        await navigator.clipboard.writeText(text)
        setKopie('kopiert')
        setTimeout(() => setKopie(null), 2000)
      } catch {
        /** Ohne Zwischenablage (Rechte, Fokus): Text zum Markieren zeigen. */
        setKopie({ text })
      }
    } catch (fehler) {
      setKopie({ text: `Bericht nicht lesbar: ${(fehler as Error).message}` })
    }
  }

  const REITER: { id: Reiter; text: string }[] = [
    { id: 'status', text: 'Status' },
    { id: 'deploys', text: 'Deploys' },
    { id: 'logs', text: 'Laufzeit-Log' },
    ...(darfAendern ? [{ id: 'variablen' as const, text: 'Variablen' }] : []),
  ]

  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-[60] flex items-start justify-center bg-[rgb(23_22_20/0.38)] p-3 pt-[4vh] sm:p-6 sm:pt-[5vh]"
      {...hintergrundSchliesst(schliessen)}
      role="dialog"
      aria-modal="true"
      aria-label={`Coolify – ${label}`}
    >
      <div className="flex h-[min(88vh,100%)] w-full max-w-[960px] min-w-0 flex-col rounded-card border border-line bg-surface shadow-card">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-line px-[18px] py-3">
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 text-sm font-bold">
              {label}
              {s && <Chip art={s.status.art}>{s.status.text}</Chip>}
            </h3>
            <p className="text-[11.5px] text-muted">
              {s ? `${s.name} · ${s.branch}` : 'Coolify wird gefragt …'}
            </p>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2 text-[12px]">
            <button
              type="button"
              onClick={() => void fuerClaude()}
              disabled={kopie === 'laedt'}
              className="cursor-pointer rounded-lg border border-line2 px-2.5 py-1 font-semibold text-ink2 hover:bg-[#f7f7f5]"
              title="Status, letzte Deploys, Build-Log des fehlgeschlagenen Deploys und Fehlerzeilen aus dem Laufzeit-Log – ohne Variablen"
            >
              {kopie === 'laedt'
                ? 'wird gesammelt …'
                : kopie === 'kopiert'
                  ? '✓ kopiert'
                  : 'Für Claude kopieren'}
            </button>
            {s && (
              <a
                href={s.coolifyLink}
                target="_blank"
                rel="noreferrer"
                className="text-accent-deep hover:underline"
              >
                In Coolify ↗
              </a>
            )}
            <button
              type="button"
              onClick={schliessen}
              className="cursor-pointer rounded-lg px-2 py-1 text-ink2 hover:bg-[#f3f2ef]"
              aria-label="Schließen"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="flex gap-1 border-b border-line px-[14px] text-[12.5px]" role="tablist">
          {REITER.map((r) => (
            <button
              key={r.id}
              type="button"
              role="tab"
              aria-selected={reiter === r.id}
              onClick={() => setReiter(r.id)}
              className={`-mb-px cursor-pointer border-b-2 px-2.5 py-2 ${
                reiter === r.id
                  ? 'border-accent font-semibold text-ink'
                  : 'border-transparent text-muted hover:text-ink2'
              }`}
            >
              {r.text}
            </button>
          ))}
        </div>

        {typeof kopie === 'object' && kopie && (
          <div className="border-b border-line bg-[#f7f7f5] px-[18px] py-2">
            <p className="mb-1 text-[11.5px] text-muted">
              Die Zwischenablage ging nicht – Text markieren und kopieren:
              <button
                type="button"
                className="ml-2 cursor-pointer text-accent-deep hover:underline"
                onClick={() => setKopie(null)}
              >
                schließen
              </button>
            </p>
            <textarea
              readOnly
              className="h-28 w-full rounded-md border border-line2 p-2 font-mono text-[11px]"
              value={kopie.text}
              onFocus={(e) => e.currentTarget.select()}
            />
          </div>
        )}

        <div className="flex min-h-0 flex-1 flex-col">
          {betrieb.error && (
            <p className="px-[18px] py-3 text-[12.5px] text-bad">✗ {betrieb.error.message}</p>
          )}
          {reiter === 'status' && (
            <StatusReiter
              umgebungId={umgebungId}
              darfAendern={darfAendern}
              zuDeploys={() => setReiter('deploys')}
            />
          )}
          {reiter === 'deploys' && (
            <DeploysReiter umgebungId={umgebungId} darfAendern={darfAendern} />
          )}
          {reiter === 'logs' && <LogReiter umgebungId={umgebungId} />}
          {reiter === 'variablen' && darfAendern && <VariablenReiter umgebungId={umgebungId} />}
        </div>
      </div>
    </div>
  )
}

function useBetrieb(umgebungId: string) {
  return useQuery({
    queryKey: ['umgebung', 'betrieb', umgebungId],
    queryFn: () => trpc.umgebung.betrieb.query({ umgebungId }),
    retry: false,
  })
}

function useAktion(umgebungId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (aktion: Aktion) => trpc.umgebung.aktion.mutate({ umgebungId, aktion }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['umgebung', 'betrieb', umgebungId] })
      void queryClient.invalidateQueries({ queryKey: ['umgebung', 'live'] })
    },
  })
}

/* ── Status ────────────────────────────────────────────────────────── */

function StatusReiter({
  umgebungId,
  darfAendern,
  zuDeploys,
}: {
  umgebungId: string
  darfAendern: boolean
  zuDeploys: () => void
}) {
  const { data: s } = useBetrieb(umgebungId)
  const aktion = useAktion(umgebungId)
  const [stoppFrage, setStoppFrage] = useState(false)
  if (!s) return null
  const letzter = s.deploys[0]
  const laeuft = s.status.roh.startsWith('running') || s.status.roh.startsWith('restarting')

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-[18px] py-3.5 text-[13px]">
      <div className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
        <Feld titel="Zustand">
          {s.status.text}{' '}
          <span className="font-mono text-[11px] text-muted">{s.status.roh || '–'}</span>
        </Feld>
        <Feld titel="Adresse">
          {s.domains.length === 0
            ? '–'
            : s.domains.map((d) => (
                <a
                  key={d}
                  href={`https://${d}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mr-2 font-mono text-accent-deep hover:underline"
                >
                  {d}
                </a>
              ))}
        </Feld>
        <Feld titel="Build">
          {s.buildPack || '–'}, Port {s.ports || '–'}
        </Feld>
        <Feld titel="Letzter Deploy">
          {letzter ? (
            <button
              type="button"
              onClick={zuDeploys}
              className="cursor-pointer text-left hover:underline"
            >
              <Chip art={DEPLOY[letzter.stand].art}>{DEPLOY[letzter.stand].text}</Chip>{' '}
              {wann(letzter.begonnen)}
              {letzter.commit && (
                <span className="font-mono text-[11px] text-muted">
                  {' '}
                  · {letzter.commit.slice(0, 7)}
                </span>
              )}
            </button>
          ) : (
            <span className="text-muted">noch keiner</span>
          )}
        </Feld>
      </div>
      {letzter?.commitText && <p className="mt-1 text-[12px] text-ink2">„{letzter.commitText}“</p>}

      {darfAendern ? (
        <div className="mt-5 grid gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="primary"
              disabled={aktion.isPending}
              onClick={() => aktion.mutate('ausrollen')}
            >
              Neu ausrollen
            </Button>
            <Button
              size="sm"
              variant="soft"
              disabled={aktion.isPending}
              onClick={() => aktion.mutate('neustart')}
              title="Container neu starten, ohne neu zu bauen"
            >
              Neustart
            </Button>
            <Button
              size="sm"
              variant="soft"
              disabled={aktion.isPending}
              onClick={() => aktion.mutate('ohneCache')}
              title="Alles neu bauen, ohne Docker-Cache – wenn sich Abhängigkeiten verklemmt haben"
            >
              Ohne Cache neu bauen
            </Button>
            {laeuft ? (
              stoppFrage ? (
                <span className="flex items-center gap-1.5 text-[12px]">
                  <span className="text-muted">Wirklich stoppen? Die Seite ist dann offline.</span>
                  <button
                    type="button"
                    className="cursor-pointer font-semibold text-bad hover:underline"
                    onClick={() => {
                      setStoppFrage(false)
                      aktion.mutate('stopp')
                    }}
                  >
                    stoppen
                  </button>
                  <button
                    type="button"
                    className="cursor-pointer text-muted hover:underline"
                    onClick={() => setStoppFrage(false)}
                  >
                    abbrechen
                  </button>
                </span>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={aktion.isPending}
                  onClick={() => setStoppFrage(true)}
                >
                  Stoppen
                </Button>
              )
            ) : (
              <Button
                size="sm"
                variant="soft"
                disabled={aktion.isPending}
                onClick={() => aktion.mutate('start')}
              >
                Starten
              </Button>
            )}
          </div>
          {aktion.isPending && <p className="text-[12px] text-muted">Coolify wird beauftragt …</p>}
          {aktion.error && <p className="text-[12px] text-bad">✗ {aktion.error.message}</p>}
          {aktion.isSuccess && aktion.variables && (
            <p className="text-[12px] text-good-text">
              ✓ {AKTION_TEXT[aktion.variables]}{' '}
              {aktion.data.deployUuid && (
                <button
                  type="button"
                  className="cursor-pointer text-accent-deep hover:underline"
                  onClick={zuDeploys}
                >
                  Verfolgen ›
                </button>
              )}
            </p>
          )}
        </div>
      ) : (
        <p className="mt-5 text-[12px] text-muted">
          Ausrollen, Neustart und Variablen sind Sache des Teams.
        </p>
      )}
    </div>
  )
}

function Feld({ titel, children }: { titel: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="w-[92px] shrink-0 text-[11px] text-muted">{titel}</span>
      <span className="min-w-0 flex-1 break-words">{children}</span>
    </div>
  )
}

/* ── Deploys ───────────────────────────────────────────────────────── */

function DeploysReiter({ umgebungId, darfAendern }: { umgebungId: string; darfAendern: boolean }) {
  const { data: s } = useBetrieb(umgebungId)
  const [gewaehlt, setGewaehlt] = useState<string | null>(null)
  const deploys = s?.deploys ?? []
  const uuid = gewaehlt ?? deploys[0]?.uuid ?? null
  const ausListe = deploys.find((d) => d.uuid === uuid)
  const aktiv = ausListe?.stand === 'wartet' || ausListe?.stand === 'laeuft'
  const queryClient = useQueryClient()

  const log = useQuery({
    queryKey: ['umgebung', 'deployLog', umgebungId, uuid],
    queryFn: () => trpc.umgebung.deployLog.query({ umgebungId, deployUuid: uuid! }),
    enabled: Boolean(uuid),
    retry: false,
    refetchInterval: aktiv ? 3000 : false,
  })
  const abbrechen = useMutation({
    mutationFn: () => trpc.umgebung.deployAbbrechen.mutate({ umgebungId, deployUuid: uuid! }),
    onSettled: () =>
      void queryClient.invalidateQueries({ queryKey: ['umgebung', 'betrieb', umgebungId] }),
  })

  if (s?.deploysFehler)
    return <p className="px-[18px] py-3 text-[12.5px] text-bad">✗ {s.deploysFehler}</p>
  if (s && deploys.length === 0)
    return <p className="px-[18px] py-3 text-[12.5px] text-muted">Noch kein Deploy.</p>

  return (
    <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
      <div className="max-h-[min(30%,220px)] shrink-0 overflow-y-auto border-b border-line sm:max-h-none sm:w-[290px] sm:border-r sm:border-b-0">
        {deploys.map((d) => (
          <button
            key={d.uuid}
            type="button"
            onClick={() => setGewaehlt(d.uuid)}
            className={`block w-full cursor-pointer border-b border-line px-3 py-2 text-left text-[12px] last:border-b-0 ${
              d.uuid === uuid ? 'bg-[#f3f2ef]' : 'hover:bg-[#f7f7f5]'
            }`}
          >
            <span className="flex items-center gap-2">
              <Chip art={DEPLOY[d.stand].art}>{DEPLOY[d.stand].text}</Chip>
              <span className="text-muted">{wann(d.begonnen)}</span>
              <span className="ml-auto text-[11px] text-muted">
                {d.neustart ? 'Neustart' : AUSLOESER[d.ausloeser]}
              </span>
            </span>
            <span className="mt-0.5 block truncate">
              {d.commit && (
                <span className="font-mono text-[11px] text-muted">{d.commit.slice(0, 7)} </span>
              )}
              {d.commitText ?? (d.ohneCache ? 'ohne Cache' : '')}
            </span>
          </button>
        ))}
      </div>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {ausListe && (
          <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-1.5 text-[11.5px] text-muted">
            <span>
              {new Date(ausListe.begonnen ?? '').toLocaleString('de-DE')}
              {dauer(ausListe.begonnen, ausListe.geaendert) &&
                !aktiv &&
                ` · ${dauer(ausListe.begonnen, ausListe.geaendert)}`}
            </span>
            {aktiv && <span className="text-warn">aktualisiert sich alle 3 s</span>}
            {darfAendern && aktiv && (
              <button
                type="button"
                disabled={abbrechen.isPending}
                onClick={() => abbrechen.mutate()}
                className="ml-auto cursor-pointer font-semibold text-bad hover:underline"
              >
                Deploy abbrechen
              </button>
            )}
            {abbrechen.error && <span className="text-bad">✗ {abbrechen.error.message}</span>}
          </div>
        )}
        {log.error && <p className="px-3 py-2 text-[12px] text-bad">✗ {log.error.message}</p>}
        {log.isPending && uuid && <p className="px-3 py-2 text-[12px] text-muted">Log lädt …</p>}
        {log.data && (
          <LogAnsicht
            zeilen={log.data.zeilen}
            ende={aktiv}
            leer="Dieser Deploy hat kein Log."
            fehlerSprung={log.data.stand === 'fehler'}
          />
        )}
      </div>
    </div>
  )
}

/* ── Laufzeit-Log ──────────────────────────────────────────────────── */

function LogReiter({ umgebungId }: { umgebungId: string }) {
  const [zeilen, setZeilen] = useState(200)
  const [mitlaufen, setMitlaufen] = useState(false)
  const log = useQuery({
    queryKey: ['umgebung', 'laufzeitLog', umgebungId, zeilen],
    queryFn: () => trpc.umgebung.laufzeitLog.query({ umgebungId, zeilen }),
    retry: false,
    refetchInterval: mitlaufen ? 5000 : false,
  })
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-3 border-b border-line px-3 py-1.5 text-[12px]">
        <label className="flex items-center gap-1.5">
          letzte
          <select
            className="rounded-md border border-line2 px-1.5 py-0.5"
            value={zeilen}
            onChange={(e) => setZeilen(Number(e.target.value))}
          >
            {[100, 200, 500, 1000, 3000].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          Zeilen
        </label>
        <label className="flex cursor-pointer items-center gap-1">
          <input
            type="checkbox"
            checked={mitlaufen}
            onChange={(e) => setMitlaufen(e.target.checked)}
          />
          mitlaufen (alle 5 s)
        </label>
        <button
          type="button"
          onClick={() => void log.refetch()}
          className="cursor-pointer text-accent-deep hover:underline"
        >
          {log.isFetching ? 'lädt …' : 'aktualisieren'}
        </button>
      </div>
      {log.error && <p className="px-3 py-2 text-[12px] text-bad">✗ {log.error.message}</p>}
      {log.isPending && <p className="px-3 py-2 text-[12px] text-muted">Log lädt …</p>}
      {log.data && (
        <LogAnsicht
          zeilen={log.data}
          ende
          leer="Der Container hat nichts geschrieben (oder läuft nicht)."
          zeitAnzeigen
        />
      )}
    </div>
  )
}

/* ── Log-Ansicht (gemeinsam) ───────────────────────────────────────── */

function LogAnsicht({
  zeilen,
  ende,
  leer,
  fehlerSprung = false,
  zeitAnzeigen = false,
}: {
  zeilen: { text: string; fehler: boolean; zeit: string | null }[]
  /** An das Ende scrollen, wenn neue Zeilen kommen. */
  ende: boolean
  leer: string
  /** Bei einem fehlgeschlagenen Deploy zur ersten Fehlerzeile springen. */
  fehlerSprung?: boolean
  zeitAnzeigen?: boolean
}) {
  const [filter, setFilter] = useState('')
  const [nurFehler, setNurFehler] = useState(false)
  const [kopiert, setKopiert] = useState(false)
  const kasten = useRef<HTMLDivElement>(null)
  const sichtbar = useMemo(() => {
    const f = filter.trim().toLowerCase()
    return zeilen
      .map((z, i) => ({ ...z, i }))
      .filter((z) => (!nurFehler || z.fehler) && (!f || z.text.toLowerCase().includes(f)))
  }, [zeilen, filter, nurFehler])
  const ersterFehler = sichtbar.find((z) => z.fehler)?.i

  const springen = () => {
    const el = kasten.current?.querySelector(`[data-zeile="${ersterFehler}"]`)
    el?.scrollIntoView({ block: 'center' })
  }
  useEffect(() => {
    const k = kasten.current
    if (!k) return
    if (fehlerSprung && ersterFehler !== undefined) springen()
    else if (ende) k.scrollTop = k.scrollHeight
  }, [zeilen.length, fehlerSprung])

  const text = sichtbar
    .map((z) => (z.zeit ? `${z.zeit.slice(0, 19)} ${z.text}` : z.text))
    .join('\n')

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-3 px-3 py-1.5 text-[12px]">
        <input
          className="min-w-[140px] flex-1 rounded-md border border-line2 px-2 py-0.5"
          placeholder="Filtern …"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <label className="flex cursor-pointer items-center gap-1">
          <input
            type="checkbox"
            checked={nurFehler}
            onChange={(e) => setNurFehler(e.target.checked)}
          />
          nur Fehler
        </label>
        {ersterFehler !== undefined && (
          <button
            type="button"
            onClick={springen}
            className="cursor-pointer text-bad hover:underline"
          >
            zur ersten Fehlerzeile
          </button>
        )}
        <button
          type="button"
          className="cursor-pointer text-accent-deep hover:underline"
          onClick={() => {
            void navigator.clipboard
              ?.writeText(text)
              .then(() => {
                setKopiert(true)
                setTimeout(() => setKopiert(false), 1500)
              })
              .catch(() => undefined)
          }}
        >
          {kopiert ? 'kopiert' : 'kopieren'}
        </button>
        <span className="text-[11px] text-muted">
          {sichtbar.length === zeilen.length
            ? `${zeilen.length} Zeilen`
            : `${sichtbar.length} von ${zeilen.length}`}
        </span>
      </div>
      <div
        ref={kasten}
        className="min-h-0 flex-1 overflow-auto bg-[#1d1c1a] px-3 py-2 font-mono text-[11.5px] leading-[1.5] text-[#e8e6e1]"
      >
        {zeilen.length === 0 ? (
          <span className="text-[#a9a59c]">{leer}</span>
        ) : (
          sichtbar.map((z) => (
            <div
              key={z.i}
              data-zeile={z.i}
              className={`whitespace-pre-wrap break-all ${z.fehler ? 'bg-[rgb(220_60_50/0.18)] text-[#ffb4a8]' : ''}`}
            >
              {zeitAnzeigen && z.zeit && (
                <span className="mr-2 text-[#8d897f] select-none">
                  {new Date(z.zeit).toLocaleTimeString('de-DE')}
                </span>
              )}
              {z.text}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

/* ── Variablen ─────────────────────────────────────────────────────── */

function VariablenReiter({ umgebungId }: { umgebungId: string }) {
  const queryClient = useQueryClient()
  const liste = useQuery({
    queryKey: ['umgebung', 'variablen', umgebungId],
    queryFn: () => trpc.umgebung.variablen.query({ umgebungId }),
    retry: false,
    staleTime: 0,
  })
  const [offen, setOffen] = useState<Set<string>>(new Set())
  const [bearbeiten, setBearbeiten] = useState<{ key: string; wert: string; neu: boolean } | null>(
    null,
  )
  const [loeschFrage, setLoeschFrage] = useState<string | null>(null)
  const [geaendert, setGeaendert] = useState(false)
  const neuLaden = () =>
    void queryClient.invalidateQueries({ queryKey: ['umgebung', 'variablen', umgebungId] })

  const setzen = useMutation({
    mutationFn: (v: { key: string; wert: string }) =>
      trpc.umgebung.variableSetzen.mutate({ umgebungId, ...v }),
    onSuccess: () => {
      setBearbeiten(null)
      setGeaendert(true)
      neuLaden()
    },
  })
  const loeschen = useMutation({
    mutationFn: (envUuid: string) => trpc.umgebung.variableLoeschen.mutate({ umgebungId, envUuid }),
    onSuccess: () => {
      setLoeschFrage(null)
      setGeaendert(true)
      neuLaden()
    },
  })
  const aktion = useAktion(umgebungId)

  const vorhanden = new Set((liste.data ?? []).map((v) => v.key))
  const neuerName = bearbeiten?.neu ? bearbeiten.key.trim() : ''
  const nameFalsch = bearbeiten?.neu && !/^[A-Za-z_][A-Za-z0-9_]*$/.test(neuerName)

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-[18px] py-3 text-[12.5px]">
      {geaendert && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-[10px] bg-[#fdf1ea] px-3 py-2 text-[12px] text-warn">
          <span>Coolify übernimmt geänderte Werte erst mit dem nächsten Deploy.</span>
          {aktion.isSuccess ? (
            <span className="font-semibold text-good-text">✓ Ausrollen angestoßen</span>
          ) : (
            <Button
              size="sm"
              variant="primary"
              disabled={aktion.isPending}
              onClick={() => aktion.mutate('ausrollen')}
            >
              {aktion.isPending ? 'wird beauftragt …' : 'Jetzt neu ausrollen'}
            </Button>
          )}
          {aktion.error && <span className="text-bad">✗ {aktion.error.message}</span>}
        </div>
      )}
      {liste.error && <p className="text-bad">✗ {liste.error.message}</p>}
      {liste.isPending && <p className="text-muted">Variablen werden gelesen …</p>}
      {liste.data && (
        <div className="overflow-hidden rounded-[11px] border border-line">
          {liste.data.length === 0 && (
            <p className="px-3 py-2 text-muted">Keine Variablen an dieser Anwendung.</p>
          )}
          {liste.data.map((v) =>
            bearbeiten && !bearbeiten.neu && bearbeiten.key === v.key ? (
              <Bearbeiten
                key={v.uuid}
                name={v.key}
                wert={bearbeiten.wert}
                setWert={(wert) => setBearbeiten({ ...bearbeiten, wert })}
                speichern={() => setzen.mutate({ key: v.key, wert: bearbeiten.wert })}
                abbrechen={() => setBearbeiten(null)}
                laeuft={setzen.isPending}
              />
            ) : (
              <div
                key={v.uuid}
                className="flex items-center gap-2 border-t border-line px-3 py-1.5 first:border-t-0"
              >
                <span className="w-[36%] min-w-0 truncate font-mono text-[11.5px]" title={v.key}>
                  {v.key}
                </span>
                <span className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-ink2">
                  {v.gesperrt ? (
                    <span className="text-muted">🔒 in Coolify gesperrt</span>
                  ) : v.wert === null ? (
                    <span className="text-muted">nicht lesbar</span>
                  ) : offen.has(v.uuid) ? (
                    v.wert || <span className="text-muted">(leer)</span>
                  ) : (
                    '••••••••'
                  )}
                </span>
                {!v.gesperrt && v.wert !== null && (
                  <button
                    type="button"
                    className="cursor-pointer text-[11.5px] text-muted hover:underline"
                    onClick={() => {
                      const n = new Set(offen)
                      if (n.has(v.uuid)) n.delete(v.uuid)
                      else n.add(v.uuid)
                      setOffen(n)
                    }}
                  >
                    {offen.has(v.uuid) ? 'verbergen' : 'zeigen'}
                  </button>
                )}
                <button
                  type="button"
                  className="cursor-pointer text-[11.5px] text-accent-deep hover:underline"
                  onClick={() => setBearbeiten({ key: v.key, wert: v.wert ?? '', neu: false })}
                >
                  ändern
                </button>
                {loeschFrage === v.uuid ? (
                  <span className="flex items-center gap-1.5 text-[11.5px]">
                    <button
                      type="button"
                      className="cursor-pointer font-semibold text-bad hover:underline"
                      disabled={loeschen.isPending}
                      onClick={() => loeschen.mutate(v.uuid)}
                    >
                      löschen
                    </button>
                    <button
                      type="button"
                      className="cursor-pointer text-muted hover:underline"
                      onClick={() => setLoeschFrage(null)}
                    >
                      nein
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="cursor-pointer text-[11.5px] text-muted hover:text-bad hover:underline"
                    onClick={() => setLoeschFrage(v.uuid)}
                  >
                    ✕
                  </button>
                )}
              </div>
            ),
          )}
          {bearbeiten?.neu && (
            <Bearbeiten
              name={bearbeiten.key}
              setName={(key) => setBearbeiten({ ...bearbeiten, key })}
              wert={bearbeiten.wert}
              setWert={(wert) => setBearbeiten({ ...bearbeiten, wert })}
              speichern={() => setzen.mutate({ key: neuerName, wert: bearbeiten.wert })}
              abbrechen={() => setBearbeiten(null)}
              laeuft={setzen.isPending}
              sperre={
                !neuerName
                  ? 'Name fehlt'
                  : nameFalsch
                    ? 'Name: Buchstaben, Ziffern, _'
                    : vorhanden.has(neuerName)
                      ? 'gibt es schon – dort „ändern“'
                      : null
              }
            />
          )}
        </div>
      )}
      {(setzen.error || loeschen.error) && (
        <p className="mt-2 text-bad">✗ {(setzen.error ?? loeschen.error)?.message}</p>
      )}
      {liste.data && !bearbeiten && (
        <button
          type="button"
          className="mt-2 cursor-pointer text-[12px] text-accent-deep hover:underline"
          onClick={() => setBearbeiten({ key: '', wert: '', neu: true })}
        >
          + Variable hinzufügen
        </button>
      )}
      <p className="mt-3 text-[11px] text-muted">
        Die Werte kommen direkt aus Coolify und gehen direkt dorthin – Work speichert sie nicht.
        Preview-Variablen und Schalter wie „nur beim Bauen“ bleiben in Coolify.
      </p>
    </div>
  )
}

function Bearbeiten({
  name,
  setName,
  wert,
  setWert,
  speichern,
  abbrechen,
  laeuft,
  sperre = null,
}: {
  name: string
  setName?: (n: string) => void
  wert: string
  setWert: (w: string) => void
  speichern: () => void
  abbrechen: () => void
  laeuft: boolean
  sperre?: string | null
}) {
  return (
    <div className="grid gap-1.5 border-t border-line bg-[#f7f7f5] px-3 py-2 first:border-t-0">
      {setName ? (
        <input
          autoFocus
          className="rounded-md border border-line2 px-2 py-1 font-mono text-[11.5px]"
          placeholder="NAME"
          value={name}
          onChange={(e) => setName(e.target.value.toUpperCase().replace(/\s/g, '_'))}
        />
      ) : (
        <span className="font-mono text-[11.5px] font-semibold">{name}</span>
      )}
      <textarea
        autoFocus={!setName}
        rows={wert.includes('\n') ? 4 : 1}
        className="rounded-md border border-line2 px-2 py-1 font-mono text-[11.5px]"
        placeholder="Wert"
        value={wert}
        onChange={(e) => setWert(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !sperre) speichern()
        }}
      />
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="primary"
          disabled={laeuft || Boolean(sperre)}
          onClick={speichern}
        >
          {laeuft ? 'speichert …' : 'Speichern'}
        </Button>
        <button
          type="button"
          className="cursor-pointer text-[12px] text-muted hover:underline"
          onClick={abbrechen}
        >
          abbrechen
        </button>
        {sperre && <span className="text-[11.5px] text-muted">{sperre}</span>}
      </div>
    </div>
  )
}
